import React from 'react';
import HelpIcon from 'Utils/HelpIcon';

/**
 * One-sentence definitions for the product's own terms, keyed by id. Any label that a buyer
 * could reasonably not know gets a <Term id> (label + info icon) or a `help` prop fed from here,
 * so the same explanation appears wherever the term does.
 */
export const GLOSSARY = {
  surfaced: 'A data source that is saved but has not passed a connection test, fingerprint or discovery yet.',
  profiled: 'A fingerprint scan has read the schema and table metadata of this source. No data has been sampled.',
  discovered: 'A discovery run has sampled this source and classified the sensitive data it found.',
  secured: 'The latest masking or encryption job on this source succeeded.',
  'never-discovered': 'No discovery run has completed on this source, so it has no risk level yet.',
  'protection-funnel': 'How far each source has moved: surfaced → fingerprinted → discovered → secured. The four counts add up to the sources listed.',
  'secured-coverage': 'Sources whose latest mask or encrypt job succeeded, out of the sources those jobs can run on.',
  'risk-level': 'Critical, High, Medium or Low, from the highest-scoring sensitive data found in the latest discovery. Not yet discovered = no completed discovery.',
  'profile-scan': 'A fingerprint scan reads schema and table metadata only. It does not sample data; discovery does.',
  'pk-density': 'Share of fingerprinted tables that declare a primary key. Low values make subsetting and masking harder.',
  'schema-identity': 'Databases whose schemas match a known product (or each other) are grouped into one identity cluster.',
  'identity-anchor': 'The database whose schema this identity cluster was matched against.',
  fingerprint: 'The commercial product the fingerprinted schema most resembles, with the match confidence.',
  'open-fields': 'Distinct columns, fields or files that still hold sensitive data since the last scan.',
  'activity-window': 'Counts labelled with a window only include runs that finished inside it. Totals are not windowed.',
  'mask-eligible': 'Connections the masking service can write to. Object stores and DynamoDB are listed but cannot be masked yet.',
  inventory: 'The list of columns (or fields / files) a masking job will change, with the algorithm for each.',
  'orchestration-group': 'An ordered set of masking jobs run one after another; a failed step halts the group.',
  'fan-out': 'One masking step that writes the same inventory to several destination sources.',
  'sub-source': 'A data source created automatically under a Folder Search parent, one per folder of files that share a format.',
};

export const glossary = (id) => GLOSSARY[id] || '';

/** `label` with the glossary's explanation behind an info icon. */
export const Term = ({ id, label, children, placement, className }) => (
  <span className={`sg-term${className ? ` ${className}` : ''}`}>
    {children != null ? children : label}
    {GLOSSARY[id] ? <HelpIcon help={GLOSSARY[id]} placement={placement} testId={`term-${id}`} /> : null}
  </span>
);

export default GLOSSARY;
